import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { add, endOfDay, isSameDay, isBefore, type NormalizedInterval } from 'date-fns'
import getAcademicYear from 'teacher-ui-client/utils/dates/get-academic-year'
import getNamedDateRangeObject from 'teacher-ui-client/utils/dates/named-date-ranges'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { CalendarNav } from 'teacher-ui-client/components/dates/calendar-nav'
import { CalendarTable } from 'teacher-ui-client/components/dates/calendar-table'
import type SessionService from 'teacher-ui-client/services/session'
import { formatDate, type IntlService } from 'ember-intl'
import { concat } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { modifier } from 'ember-modifier'

interface NamedRangeViewModel {
  key: string
  label: string
  start: Date
  end: Date
}

interface Signature {
  Element: HTMLInputElement
  Args: {
    period: NormalizedInterval
    onPeriodSelected: (period: NormalizedInterval) => unknown
    isDisabled?: boolean
  }
}

const maxDays = 365

export class PeriodDatePicker extends Component<Signature> {
  @service subscriptionType!: SubscriptionTypeService

  @service session!: SessionService

  @service intl!: IntlService

  // edit our internal copy of start/end dates, until ready to commit
  @tracked start?: Date
  @tracked end?: Date

  /**
   * A modifier to update internal start/end dates if new ones arrive from outside (eg due to url change). This will
   * override any saved state.
   */
  watchDates = modifier<{ Element: HTMLElement; Args: { Positional: [Date, Date] } }>((_element, [start, end]) => {
    this.start = start
    this.end = end
  })

  /**
   * Determine the allowable range around the start date, that can be chosen for the next date. Only need to provide
   * the restricted range when the first date (start) has been chosen.
   */
  get minNextDate() {
    return this.start && !this.end ? add(this.start, { days: -maxDays }) : undefined
  }

  /**
   * @see minNextDate
   */
  get maxNextDate() {
    return this.start && !this.end ? add(this.start, { days: maxDays }) : undefined
  }

  // default today, change on named range click, or prev/next month click
  @tracked center = new Date()

  /**
   * Next center, indicates month for the right calendar
   */
  get centerNext() {
    return add(this.center, { months: 1 })
  }

  onCenterChange = (date: Date) => {
    this.center = date
  }

  /**
   * Returns an array of named range view model objects, used to create the buttons for display.
   */
  get namedRanges() {
    const now = new Date()
    const schoolYearPeriod = getAcademicYear(now, this.session.currentSchool.countryCode)
    const namedRanges = getNamedDateRangeObject(schoolYearPeriod, now)

    return Object.entries(namedRanges).reduce<NamedRangeViewModel[]>((acc, [key, namedRange]) => {
      return [
        ...acc,
        {
          key,
          label: this.intl.t(namedRange.intlKey),
          start: namedRange.start,
          end: namedRange.end,
        },
      ]
    }, [])
  }

  /**
   * Find the named range for the period start/end from args. This is displayed in the input trigger, and is unaffected
   * by temporary range selections.
   */
  get committedNamedRange() {
    const { start, end } = this.args.period
    if (!start || !end) return
    return this.namedRanges.find((namedRange) => isSameDay(namedRange.start, start) && isSameDay(namedRange.end, end))
  }

  /**
   * Find the named range for the selections made. This is used to highlight the necessary named range button within the
   * dropdown.
   */
  get selectedNamedRange() {
    const { start, end } = this
    if (!start || !end) return
    return this.namedRanges.find((namedRange) => isSameDay(namedRange.start, start) && isSameDay(namedRange.end, end))
  }

  /**
   * A named range has been chosen, adjust the center and commit the dates.
   */
  onSelectNamedRange = (closeAction: () => unknown, start: Date, end: Date) => {
    this.center = start
    this.commit(closeAction, { start, end })
  }

  /**
   * A single date has been chosen - determine how that affects the date state stored in this component.
   */
  onSelectDate = (closeAction: () => unknown, selectedDate: Date) => {
    const { start, end } = this
    // if both or neither are already set, this is a next/new selection. set start date, clear end date
    if ((start && end) || (!start && !end)) {
      this.start = selectedDate
      this.end = undefined
    } else if (start) {
      // otherwise, start date is set (but not end date), this will be either a new start date, or a new end date, depending on if it is before or after the start date.
      if (isBefore(start, selectedDate)) {
        this.start = start
        this.end = selectedDate
      } else {
        this.start = selectedDate
        this.end = start
      }
      this.commit(closeAction, { start: this.start, end: endOfDay(this.end) })
    }
  }

  /**
   * Send new dates up, close the dropdown
   */
  commit = (closeAction: () => unknown, { start, end }: NormalizedInterval) => {
    this.args.onPeriodSelected({ start, end })
    closeAction()
  }

  /**
   * If half a selection is made and then the dropdown is closed, make sure to reset the dates.
   */
  onClose = () => {
    if (!this.start || !this.end) {
      this.start = this.args.period.start
      this.end = this.args.period.end
    }
  }

  <template>
    <PopoverStandardDropdown @disabled={{@isDisabled}} @onClose={{this.onClose}} as |drop status actions|>
      <InputText
        type="text"
        readonly
        value="{{if this.committedNamedRange (concat this.committedNamedRange.label ': ')}}{{formatDate
          @period.start
        }} – {{formatDate @period.end}}"
        class="select-none"
        disabled={{@isDisabled}}
        @themeKey={{themeKey this.subscriptionType.current}}
        @icon="calendar-solid"
        @iconEnd={{true}}
        {{drop.makeTrigger}}
        {{this.watchDates @period.start @period.end}}
        ...attributes
      />
      <drop.content>
        <div
          data-test-period-date-picker-content
          class="border-dusty-black-100 my-px w-fit rounded-md border bg-white text-neutral-300 shadow-xl"
        >
          <div data-test-preset-ranges class="flex justify-center gap-2 bg-neutral-50 p-3">
            {{#each this.namedRanges as |namedRange|}}
              <button
                data-test-named-range={{namedRange.key}}
                type="button"
                {{on "click" (fn this.onSelectNamedRange actions.close namedRange.start namedRange.end)}}
                class="{{if (eq namedRange.key this.selectedNamedRange.key) 'active'}}
                  border-neutral-75 hover:bg-neutral-75 active-link:border-transparent active-link:bg-blue-100 active-link:text-blue-350 rounded-lg border bg-white px-4 py-2 text-sm transition-colors duration-300 ease-in-out active:bg-neutral-100"
              >
                {{namedRange.label}}
              </button>
            {{/each}}
          </div>

          <div class="p-3">
            <CalendarNav
              @center={{this.center}}
              @onCenterChange={{this.onCenterChange}}
              @centerNext={{this.centerNext}}
            />
            <div class="flex items-start justify-center gap-4">
              <CalendarTable
                class="basis-full"
                @center={{this.center}}
                @onSelectDate={{fn this.onSelectDate actions.close}}
                @start={{this.start}}
                @end={{this.end}}
                @min={{this.minNextDate}}
                @max={{this.maxNextDate}}
              />
              <CalendarTable
                class="basis-full"
                @center={{this.centerNext}}
                @onSelectDate={{fn this.onSelectDate actions.close}}
                @start={{this.start}}
                @end={{this.end}}
                @min={{this.minNextDate}}
                @max={{this.maxNextDate}}
              />
            </div>
          </div>
        </div>
      </drop.content>
    </PopoverStandardDropdown>
  </template>
}
